import * as THREE from "three";

/**
 * Tworzy efekt eksplozji błyskawicy w podanej pozycji
 * @param scene Scena Three.js
 * @param position Pozycja eksplozji
 * @param scale Skala eksplozji (domyślnie 1.0)
 * @returns THREE.Group zawierający wszystkie elementy eksplozji
 */
export function createLightningExplosion(
  scene: THREE.Scene, 
  position: THREE.Vector3,
  scale: number = 1.0,
  color: number = 0x4080ff, // Default color for lightning
  intensity: number = 1.0 // Intensity multiplier
): THREE.Group {
  // Kontener na wszystkie elementy eksplozji
  const explosionGroup = new THREE.Group();
  explosionGroup.position.copy(position);
  // Flaga do śledzenia czy eksplozja jest aktywna
  explosionGroup.userData.isActive = true;
  scene.add(explosionGroup);

  try {
    // Efekt eksplozji - główne światło
    const explosionLight = new THREE.PointLight(color, 6 * scale * intensity, 12 * scale);
    explosionGroup.add(explosionLight);

    // Dodatkowe światło pulsujące
    const pulseLight = new THREE.PointLight(color, 3 * scale * intensity, 8 * scale);
    explosionGroup.add(pulseLight);

    // Efekt eksplozji - cząsteczki błyskawicy
    const particleCount = Math.floor(25 * scale);
    const particles: THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>[] = [];

    // Tworzymy cząsteczki błyskawicy
    for (let i = 0; i < particleCount; i++) {
      const particleGeometry = new THREE.SphereGeometry(
        (0.04 + Math.random() * 0.12) * scale,
        8,
        8
      );
      
      // Losowy odcień niebieskiego/białego dla cząsteczek
      const blueIntensity = 0.6 + Math.random() * 0.4;
      const particleMaterial = new THREE.MeshBasicMaterial({
        color: new THREE.Color(color),
        transparent: true,
        opacity: 0.9,
      });
      const particle = new THREE.Mesh(particleGeometry, particleMaterial);

      // Losowy kierunek rozrzutu z większym rozproszeniem
      const direction = new THREE.Vector3(
        Math.random() * 2 - 1,
        Math.random() * 2.5 - 0.5, // Większy rozrzut w górę
        Math.random() * 2 - 1
      ).normalize();

      // Zapisz kierunek i prędkość jako właściwości cząsteczki
      // Cząsteczki błyskawicy poruszają się szybciej
      particle.userData = { 
        direction, 
        speed: (0.08 + Math.random() * 0.15) * scale,
        phase: Math.random() * Math.PI * 2 // Faza do efektu migotania
      };

      explosionGroup.add(particle);
      particles.push(particle);
    }

    // Dodajemy efekt "promieni" błyskawicy
    const lightningRayCount = Math.floor(6 * scale);
    const lightningRays: THREE.Mesh<THREE.BoxGeometry, THREE.MeshBasicMaterial>[] = [];
    
    for (let i = 0; i < lightningRayCount; i++) {
      // Tworzymy "promień" błyskawicy jako wydłużony prostopadłościan
      const rayLength = (1.0 + Math.random() * 2.0) * scale;
      const rayGeometry = new THREE.BoxGeometry(0.05 * scale, rayLength, 0.05 * scale);
      const rayMaterial = new THREE.MeshBasicMaterial({
        color: color,
        transparent: true,
        opacity: 0.7,
      });
      const ray = new THREE.Mesh(rayGeometry, rayMaterial);
      
      // Losowy kierunek promienia
      const direction = new THREE.Vector3(
        Math.random() * 2 - 1,
        Math.random() * 2 - 1,
        Math.random() * 2 - 1
      ).normalize();
      
      // Ustawiamy orientację promienia zgodnie z kierunkiem
      ray.position.set(direction.x * rayLength/2, direction.y * rayLength/2, direction.z * rayLength/2);
      ray.lookAt(new THREE.Vector3(0, 0, 0));
      
      // Zapisujemy dane do animacji
      ray.userData = {
        initialLength: rayLength,
        direction: direction
      };
      
      explosionGroup.add(ray);
      lightningRays.push(ray);
    }

    // Animacja eksplozji błyskawicy
    let explosionTime = 0;
    let animationFrameId: number;
    const animateExplosion = () => {
      // Sprawdź czy grupa eksplozji jest nadal w scenie i aktywna
      if (!explosionGroup.parent || !explosionGroup.userData.isActive) {
        // Cleanup jeśli usunięto ze sceny
        cancelAnimationFrame(animationFrameId);
        cleanupExplosion();
        return;
      }

      explosionTime += 0.016;

      // Zmniejszaj intensywność głównego światła
      explosionLight.intensity = Math.max(0, 6 * scale - explosionTime * 6);
      
      // Pulsujące światło
      pulseLight.intensity = Math.max(0, 3 * scale - explosionTime * 3) * 
                            (0.7 + 0.3 * Math.sin(explosionTime * 30));

      // Aktualizuj pozycje cząsteczek
      for (const particle of particles) {
        const dir = particle.userData.direction as THREE.Vector3;
        const spd = particle.userData.speed as number;
        const phase = particle.userData.phase as number;

        particle.position.add(
          new THREE.Vector3(
            dir.x * spd,
            dir.y * spd - 0.005 * scale, // Mniejsza grawitacja dla efektu błyskawicy
            dir.z * spd
          )
        );

        // Efekt migotania cząsteczek
        const flickerFactor = 0.7 + 0.3 * Math.sin(explosionTime * 20 + phase);
        (particle.material as THREE.MeshBasicMaterial).opacity = Math.max(
          0,
          0.9 * flickerFactor - explosionTime * 0.9
        );
        
        // Losowa zmiana rozmiaru dla efektu migotania
        particle.scale.set(
          1.0 + 0.2 * Math.sin(explosionTime * 15 + phase),
          1.0 + 0.2 * Math.sin(explosionTime * 15 + phase),
          1.0 + 0.2 * Math.sin(explosionTime * 15 + phase)
        );
      }
      
      // Animacja promieni błyskawicy
      for (const ray of lightningRays) {
        // Zmniejszanie długości promieni z czasem
        const initialLength = ray.userData.initialLength as number;
        const newLength = Math.max(0, initialLength * (1 - explosionTime));
        
        // Aktualizacja geometrii promienia
        ray.scale.y = newLength / initialLength;
        
        // Migotanie promieni
        (ray.material as THREE.MeshBasicMaterial).opacity = Math.max(
          0,
          0.7 - explosionTime * 0.7
        ) * (0.7 + 0.3 * Math.sin(explosionTime * 25 + Math.random()));
      }

      // Zakończ animację po 1 sekundzie
      if (explosionTime > 1) {
        // Anuluj animację
        cancelAnimationFrame(animationFrameId);
        cleanupExplosion();
        return;
      }
      animationFrameId = requestAnimationFrame(animateExplosion);
    };

    // Funkcja czyszcząca
    const cleanupExplosion = () => {
      explosionGroup.userData.isActive = false;
      // Usuń wszystkie elementy z grupy
      while (explosionGroup.children.length > 0) {
        const child = explosionGroup.children[0];
        explosionGroup.remove(child);
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          child.material.dispose();
        } else if (child instanceof THREE.Light) {
          // Cleanup zasobów światła jeśli potrzebne
        }
      }
      // Usuń grupę ze sceny jeśli nadal tam jest
      if (explosionGroup.parent) {
        scene.remove(explosionGroup);
      }
    };

    // Dodaj metodę czyszczącą do grupy do użytku zewnętrznego
    explosionGroup.userData.cleanup = cleanupExplosion;
    
    animationFrameId = requestAnimationFrame(animateExplosion);
    return explosionGroup;
  } catch (error) {
    console.error("Error creating lightning explosion effect:", error);
    scene.remove(explosionGroup);
    return explosionGroup; // Zwróć pustą grupę w przypadku błędu
  }
}